/**
 * Threads API Client
 * Based on 01-api-reference.md Thread Management API
 */

import { getAgentHeaders } from './settings';

const AGENT_API_BASE_URL = '/api/v1';

// Types
export interface Thread {
  thread_id: string;
  user_id: string;
  title: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ThreadListItem {
  thread_id: string;
  title: string | null;
  last_message_at: string | null;
  message_count: number;
  active_message_count: number;
  active_tokens: number;
  created_at: string;
}

export interface ThreadDetail extends Thread {
  message_count: number;
  last_message_at: string | null;
}

export interface PaginationInfo {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface ThreadListResponse {
  threads: ThreadListItem[];
  pagination: PaginationInfo;
}

export interface ThreadCreateRequest {
  title?: string;
  metadata?: Record<string, unknown>;
}

export interface ThreadUpdateRequest {
  title?: string;
  metadata?: Record<string, unknown>;
}

export interface ThreadListParams {
  limit?: number;
  offset?: number;
  order?: 'asc' | 'desc';
}

/** Single Gemini thinking chunk. */
export interface ThoughtChunk {
  seq: number;
  text: string;
  elapsed_ms: number;
}

export interface MessageItem {
  id: string;
  role: string;
  content: string;
  used_tools: string[];
  timestamp: string;
  source: 'active' | 'archive';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- server-side dynamic data
  tool_ui?: Record<string, any> | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- server-side dynamic data
  metadata?: Record<string, any> | null;
  /** Only present when `?include=thoughts` query parameter is set. */
  thoughts?: ThoughtChunk[] | null;
}

export interface MessageListResponse {
  thread_id: string;
  messages: MessageItem[];
  pagination: PaginationInfo;
  summary: string | null;
  archive_total?: number;
  generation_status?: string;
}

export interface CloneThreadResponse {
  thread_id: string;
  user_id: string;
}

/**
 * Threads API
 */
export const threadsApi = {
  /**
   * Create a new thread for a user
   */
  async create(userId: string, request: ThreadCreateRequest = {}): Promise<Thread> {
    const response = await fetch(`${AGENT_API_BASE_URL}/users/${userId}/threads`, {
      method: 'POST',
      headers: await getAgentHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail?.detail || error.detail || 'Failed to create thread');
    }

    return response.json();
  },

  /**
   * List threads for a user
   */
  async listByUser(userId: string, params: ThreadListParams = {}): Promise<ThreadListResponse> {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.order) queryParams.append('order', params.order);

    const url = `${AGENT_API_BASE_URL}/users/${userId}/threads${
      queryParams.toString() ? '?' + queryParams.toString() : ''
    }`;
    const response = await fetch(url, {
      headers: await getAgentHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to list threads: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Get thread detail by ID
   */
  async getById(threadId: string): Promise<ThreadDetail> {
    const response = await fetch(`${AGENT_API_BASE_URL}/threads/${threadId}`, {
      headers: await getAgentHeaders(),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Thread not found');
      }
      throw new Error(`Failed to get thread: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Update thread
   */
  async update(threadId: string, data: ThreadUpdateRequest): Promise<Thread> {
    const response = await fetch(`${AGENT_API_BASE_URL}/threads/${threadId}`, {
      method: 'PATCH',
      headers: await getAgentHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail?.detail || error.detail || 'Failed to update thread');
    }

    return response.json();
  },

  /**
   * Delete thread and related data
   */
  async delete(threadId: string): Promise<void> {
    const response = await fetch(`${AGENT_API_BASE_URL}/threads/${threadId}`, {
      method: 'DELETE',
      headers: await getAgentHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete thread: ${response.statusText}`);
    }
  },

  /**
   * Clone a thread (debug) — deep-copies messages, archives, and LangGraph
   * checkpoints under a new thread_id within the source thread's owning user.
   * No body (always clones within the owner). Calls agent-server directly via
   * /api/v1 (nginx 300s); a busy thread takes a few seconds.
   */
  async clone(threadId: string): Promise<CloneThreadResponse> {
    const response = await fetch(`${AGENT_API_BASE_URL}/threads/${threadId}/clone`, {
      method: 'POST',
      headers: await getAgentHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail?.detail || error.detail || 'Failed to clone thread');
    }

    return response.json();
  },

  /**
   * Get messages for a thread
   */
  async getMessages(
    threadId: string,
    params: {
      limit?: number;
      offset?: number;
      order?: 'asc' | 'desc';
      source?: 'active' | 'archive' | 'all';
      /** Comma-separated extra fields. Currently supports `thoughts` (admin/analysis only). */
      include?: string;
    } = {},
  ): Promise<MessageListResponse> {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset !== undefined && params.offset > 0) queryParams.append('offset', params.offset.toString());
    if (params.order) queryParams.append('order', params.order);
    if (params.source) queryParams.append('source', params.source);
    if (params.include) queryParams.append('include', params.include);

    const url = `${AGENT_API_BASE_URL}/threads/${threadId}/messages${
      queryParams.toString() ? '?' + queryParams.toString() : ''
    }`;
    const response = await fetch(url, {
      headers: await getAgentHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get messages: ${response.statusText}`);
    }

    return response.json();
  },
};

export default threadsApi;
